(() => {
  class LogoMarquee {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-marquee-viewport]');
      this.track = section.querySelector('[data-marquee-track]');

      if (!this.viewport || !this.track) {
        return;
      }

      this.settings = {
        autoplay: section.dataset.autoplay === 'true',
        draggable: section.dataset.draggable === 'true',
        pauseHover: section.dataset.pauseHover === 'true',
        direction: section.dataset.direction || 'left',
        speed: Number(section.dataset.speed) || 30
      };

      this.originalGroup = null;
      this.loopWidth = 0;
      this.position = 0;
      this.velocity = 0;
      this.animationFrame = null;
      this.lastTime = null;
      this.paused = false;
      this.dragging = false;
      this.startX = 0;
      this.startPosition = 0;
      this.hasMoved = false;
      this.clones = [];
      this.resizeObserver = null;
      this.init();
    }

    init() {
      this.createGroup();
      this.buildLoop();
      this.bindEvents();
      this.setupResizeObserver();
      this.section.classList.add('is-initialized');
      if (this.settings.autoplay) {
        this.start();
      }
    }

    createGroup() {
      const group = document.createElement('div');
      group.className = 'logo-marquee__group';

      while (this.track.firstChild) {
        group.appendChild(this.track.firstChild);
      }

      this.track.appendChild(group);
      this.originalGroup = group;
    }

    buildLoop() {
      this.removeClones();
      requestAnimationFrame(() => {
        this.loopWidth = this.originalGroup.offsetWidth;
        if (!this.loopWidth) {
          return;
        }

        const requiredWidth = this.viewport.offsetWidth + this.loopWidth;
        let currentWidth = this.loopWidth;
        while (currentWidth < requiredWidth) {
          const clone = this.originalGroup.cloneNode(true);
          clone.dataset.clone = 'true';
          this.track.appendChild(clone);
          this.clones.push(clone);
          currentWidth += this.loopWidth;
        }

        this.applyTransform();
      });
    }

    removeClones() {
      this.clones.forEach(clone => {
        clone.remove();
      });

      this.clones = [];
    }

    start() {
      this.lastTime = performance.now();
      this.animate();
    }

    animate(time = performance.now()) {
      if (!this.lastTime) {
        this.lastTime = time;
      }

      const delta = time - this.lastTime;
      this.lastTime = time;

      if (!this.paused && !this.dragging) {
        const movement = (this.settings.speed / 100) * (delta / 16);
        if (this.settings.direction === 'right') {
          this.position += movement;
        }
        else {
          this.position -= movement;
        }

        this.normalize();
      }

      this.applyTransform();
      this.animationFrame = requestAnimationFrame(this.animate.bind(this));
    }

    normalize() {
      if (!this.loopWidth) {
        return;
      }

      if (this.position <= -this.loopWidth) {
        this.position += this.loopWidth;
      }

      if (this.position >= this.loopWidth) {
        this.position -= this.loopWidth;
      }
    }

    applyTransform() {
      this.track.style.transform = `translate3d(${this.position}px,0,0)`;
    }

    bindEvents() {
      if (this.settings.pauseHover) {
        this.viewport.addEventListener(
          'mouseenter',
          () => {
            this.paused = true;
          }
        );

        this.viewport.addEventListener(
          'mouseleave',
          () => {
            this.paused = false;
          }
        );
      }

      if (this.settings.draggable) {
        this.viewport.addEventListener('pointerdown', this.pointerDown.bind(this));
        window.addEventListener(
          'pointermove',
          this.pointerMove.bind(this),
          {
            passive:false
          }
        );

        window.addEventListener(
          'pointerup',
          this.pointerUp.bind(this)
        );
      }

      window.addEventListener(
        'resize',
        () => {
          this.buildLoop();
        }
      );
    }

    pointerDown(event) {
      this.dragging = true;
      this.hasMoved = false;
      this.startX = event.clientX;
      this.startPosition = this.position;
      this.viewport.classList.add('is-dragging');
    }

    pointerMove(event) {
      if (!this.dragging) {
        return;
      }

      event.preventDefault();
      const distance = event.clientX - this.startX;

      if (Math.abs(distance) > 5) {
        this.hasMoved = true;
      }

      this.position = this.startPosition + distance;
      this.normalize();
      this.applyTransform();
    }

    pointerUp() {
      this.dragging = false;
      this.viewport.classList.remove('is-dragging');
    }

    setupResizeObserver() {
      if (!window.ResizeObserver) {
        return;
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.buildLoop();
      });

      this.resizeObserver.observe(this.viewport);
    }

    destroy() {
      cancelAnimationFrame(this.animationFrame);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.removeClones();
      this.track.style.transform = '';
    }
  }

  const instances = new Map();
  function init(section) {
    if (instances.has(section.id)) {
      return;
    }

    instances.set(section.id, new LogoMarquee(section));
  }

  function destroy(section) {
    const instance = instances.get(section.id);
    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(section.id);
  }

  function initAll() {
    document
      .querySelectorAll('.logo-marquee')
      .forEach(section => {
        init(section);
      });
  }

  document.addEventListener('shopify:section:load', event => {
    const section = event.target.querySelector('.logo-marquee');
    if (section) {
      init(section);
    }
  }
);

document.addEventListener('shopify:section:unload', event => {
  const section = event.target.querySelector('.logo-marquee');
  if (section) {
    destroy(section);
  }
});

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  }
  else {
    initAll();
  }
})();